import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { PermissionTree, initialGrants } from '@/features/tenantAdmin/identityUsers/PermissionTree';
import {
  useCreateRole,
  useRole,
  useRolePermissions,
  useUpdateRole,
  useUpdateRolePermissions,
} from './api';

// Permissions for a not-yet-created role load fine (ABP returns the full tree,
// all ungranted) under any providerKey, so we use a placeholder for create.
const NEW_ROLE_PERM_KEY = '__new_role__';
const ROLES_PATH = '/admin/identity/roles';

// Create / edit a role on a dedicated page WITH its permissions directly on the
// page (mirrors the legacy Roles flow — not a modal + separate permissions).
export function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  const roleQuery = useRole(isEdit ? id! : null);
  const roleName = roleQuery.data?.name ?? null;
  const permQuery = useRolePermissions(isEdit ? roleName : NEW_ROLE_PERM_KEY);

  const [form, setForm] = useState({ name: '', isDefault: false, isPublic: false });
  const [grants, setGrants] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (roleQuery.data) {
      setForm({ name: roleQuery.data.name, isDefault: roleQuery.data.isDefault, isPublic: roleQuery.data.isPublic });
    }
  }, [roleQuery.data]);
  useEffect(() => {
    if (permQuery.data) setGrants(initialGrants(permQuery.data));
  }, [permQuery.data]);

  const createMut = useCreateRole();
  const updateMut = useUpdateRole();
  const updatePermMut = useUpdateRolePermissions();
  const busy = createMut.isPending || updateMut.isPending || updatePermMut.isPending;

  async function save() {
    const name = form.name.trim();
    if (!name) {
      toast.error('Role name is required.');
      return;
    }
    if (!permQuery.data) return;
    const permissions = {
      permissions: permQuery.data.groups
        .flatMap((g) => g.permissions)
        .map((p) => ({ name: p.name, isGranted: grants[p.name] ?? false })),
    };
    try {
      if (isEdit) {
        await updateMut.mutateAsync({
          id: id!,
          input: { name, isDefault: form.isDefault, isPublic: form.isPublic, concurrencyStamp: roleQuery.data?.concurrencyStamp },
        });
      } else {
        await createMut.mutateAsync({ name, isDefault: form.isDefault, isPublic: form.isPublic });
      }
      await updatePermMut.mutateAsync({ roleName: name, input: permissions });
      toast.success(isEdit ? 'Role updated successfully' : 'Role created successfully');
      navigate(ROLES_PATH);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to save the role.');
    }
  }

  const [filter, setFilter] = useState('');
  // The admin role is a static/built-in role and must not be editable.
  const locked = isEdit && roleQuery.data?.isStatic === true;

  const loading = (isEdit && roleQuery.isLoading) || permQuery.isLoading;
  const error = (isEdit && roleQuery.isError) || permQuery.isError;

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Role' : 'New Role'}
        breadcrumb={['Roles', isEdit ? 'Edit Role' : 'New Role']}
      />
      {loading ? (
        <div className="card custom-card">
          <div className="card-body">
            <Spinner />
          </div>
        </div>
      ) : error ? (
        <div className="card custom-card">
          <div className="card-body">
            <ErrorState
              onRetry={() => {
                roleQuery.refetch();
                permQuery.refetch();
              }}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Role name */}
          <div className="card custom-card mb-3">
            <div className="card-body">
              <div>
                <label className="form-label" htmlFor="role-name">
                  Role name
                </label>
                <input
                  id="role-name"
                  className="form-control"
                  maxLength={256}
                  autoFocus
                  disabled={locked}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Permissions — filter (left) + Cancel/Save (right) in the header */}
          <div className="card custom-card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="input-group" style={{ maxWidth: 360 }}>
                <span className="input-group-text">
                  <i className="fa fa-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter permissions..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-dark" onClick={() => navigate(ROLES_PATH)} disabled={busy}>
                  Cancel
                </button>
                {!locked && (
                  <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
                    <i className="fa fa-check me-1" />
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
            </div>
            <div className="card-body">
              {permQuery.data && (
                <PermissionTree result={permQuery.data} grants={grants} onToggle={setGrants} disabled={locked} filter={filter} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
